import { Button, Flex, HStack, Stack, Text, useToast } from "@chakra-ui/react";
import { useTranslation } from "react-i18next";
import { FiEdit2 } from "react-icons/fi";
import { useNavigate } from "react-router-dom";
import {
  NegotiationRoundStatus,
  useCompleteNegotiationRoundV2Mutation,
  useReopenNegotiationRoundV2Mutation,
  type NegotiationRoundV2Dto,
} from "../../../../autogen/bff-api";
import { DocumentsAsOwnerTable } from "../../../../common/documents/DocumentsAsOwnerTable";
import { useApiError } from "../../../../common/errors/useApiError";
import { convertISOToDate } from "../../../../common/input/DateTimeSelectors/ISODateSelector";
import { isTiptapContentEmpty, TipTap } from "../../../../common/input/TipTap/TipTap";
import { useConfirmationModal } from "../../../../common/useConfirmationModal";
import { urls } from "../../../../urls";
import { RoundInvitesList } from "./RoundInvitesList";

interface Props {
  round: NegotiationRoundV2Dto;
  lifecycleLocked?: boolean;
}

export const RoundSummary = ({ round, lifecycleLocked = false }: Props) => {
  const { t } = useTranslation();
  const { trigger } = useApiError();
  const toast = useToast();
  const navigate = useNavigate();
  const confirmationModal = useConfirmationModal();

  const [completeRound, { isLoading: isCompleting }] = useCompleteNegotiationRoundV2Mutation();
  const [reopenRound, { isLoading: isReopening }] = useReopenNegotiationRoundV2Mutation();

  const isDraft = round.status === NegotiationRoundStatus.Draft;
  const isPublished = round.status === NegotiationRoundStatus.Published;
  const isCompleted = round.status === NegotiationRoundStatus.Completed;

  const handleComplete = () => {
    confirmationModal.show({
      title: t("Complete negotiation round?"),
      description: t("Suppliers will no longer be able to submit bids for this round."),
      confirmButtonText: t("Complete round") ?? "Complete round",
      onConfirmed: async () => {
        try {
          await completeRound({ eventId: round.eventId, roundId: round.id }).unwrap();
          toast({ title: t("Negotiation round completed"), status: "success" });
        } catch (e) {
          trigger(e);
        }
      },
    });
  };

  const handleReopen = () => {
    confirmationModal.show({
      title: t("Reopen negotiation round?"),
      description: t("Suppliers will again be able to submit bids for this round."),
      confirmButtonText: t("Reopen") ?? "Reopen",
      onConfirmed: async () => {
        try {
          await reopenRound({ eventId: round.eventId, roundId: round.id }).unwrap();
          toast({ title: t("Negotiation round reopened"), status: "success" });
        } catch (e) {
          trigger(e);
        }
      },
    });
  };

  return (
    <Stack spacing="5" pt="4">
      {confirmationModal.modal}

      <Stack spacing="2">
        {round.title && <Text fontWeight="medium">{round.title}</Text>}
        <HStack spacing="3">
          <Text fontSize="sm" color="smMuted">
            {t("Deadline")}: {convertISOToDate(round.deadline) || "–"}
          </Text>
          {round.isFinalRound && (
            <Text fontSize="sm" color="smMuted">
              {t("Final Round")}
            </Text>
          )}
        </HStack>
        {round.description && !isTiptapContentEmpty(round.description) && (
          <TipTap content={round.description} editable={false} hasBorder={false} padding="0" />
        )}
      </Stack>

      {round.documents.length > 0 && (
        <Stack spacing="2">
          <Text fontSize="sm" fontWeight="medium" color="smText">
            {t("Documents")} ({round.documents.length})
          </Text>
          <DocumentsAsOwnerTable documents={round.documents} />
        </Stack>
      )}

      <Stack spacing="2">
        <Text fontSize="sm" fontWeight="medium" color="smText">
          {t("Invited organizations")} ({round.invites.length})
        </Text>
        <RoundInvitesList invites={round.invites} />
      </Stack>

      {round.internalConversation.length > 0 && (
        <Stack spacing="2">
          <Text fontSize="sm" fontWeight="medium" color="smMuted">
            {t("Internal conversation")} ({round.internalConversation.length})
          </Text>
          <Stack spacing="2">
            {round.internalConversation.map((msg) => (
              <Text key={msg.id} fontSize="sm" color="smMuted" bg="smBackgroundSecondary" p="2" rounded="md">
                {msg.message}
              </Text>
            ))}
          </Stack>
        </Stack>
      )}

      <Flex gap="3" pt="2" flexWrap="wrap">
        {!lifecycleLocked && (isDraft || isPublished) && (
          <Button
            colorScheme="teal"
            variant={isDraft ? "solid" : "outline"}
            size="sm"
            leftIcon={<FiEdit2 />}
            onClick={() => navigate(urls.v2.sourcingEvents.editNegotiationRound.go(round.eventId, round.id))}
          >
            {t("Edit round")}
          </Button>
        )}
        {!lifecycleLocked && isPublished && (
          <Button colorScheme="orange" variant="outline" size="sm" onClick={handleComplete} isLoading={isCompleting}>
            {t("Complete round")}
          </Button>
        )}
        {!lifecycleLocked && isCompleted && (
          <Button colorScheme="blue" variant="outline" size="sm" onClick={handleReopen} isLoading={isReopening}>
            {t("Reopen round")}
          </Button>
        )}
      </Flex>
    </Stack>
  );
};
